import React, { useState } from 'react'
import c from 'classnames'
import gql from "graphql-tag"
import Placeholder from '@/components/common/placeholder'
import { useGlobalContext } from '@/components/context'
import { canManageTeams } from '@/utils/permissions'
import buttonStyle from '@sass/common/button.module.sass'
import iconStyle from '@sass/common/icon.module.sass'

import ModuleItem from './module-item'
import style from './index.module.sass'
import { UserToManageQuery } from '../__gen/edit'
import { TeamsToAssignQuery, useTeamsToAssignQuery } from './__gen'

type UserType = NonNullable<UserToManageQuery["user"]>
type TeamType = NonNullable<TeamsToAssignQuery["team_List"]>["objects"][number]


gql`
	query teamsToAssign($userId: ID!) {
		team_List(
			limit: 200,
			offset: 0,
			orderBy: [{field: module_Name}, {field: name}]
		) {
			objects {
				id
				name
				module {
					id
					name
					icon
					teamWord
				}
				memberId(userId: $userId)
			}
		}
		userModule_List(user: $userId) {
			objects {
				id
				module {
					id
				}
			}
		}
	}
`

// Teams are ordered by module, so each module keeps its place in the list
const groupByModule = (teams: TeamType[]) => {
	const groups: {module: TeamType["module"], teams: TeamType[]}[] = []
	teams.forEach(team => {
		const group = groups.find(g => g.module.id === team.module.id)
		if (group) {
			group.teams.push(team)
		} else {
			groups.push({module: team.module, teams: [team]})
		}
	})
	return groups
}

type Props = {
	user: UserType
}

const Header = ({editable, onToggle}: {editable?: boolean, onToggle?: () => void}) => (
	<div className={style.header}>
		<h4>Access</h4>
		{onToggle &&
			<button
				className={c(
					buttonStyle.small,
					editable ? buttonStyle.primarySolid : c(buttonStyle.grey, iconStyle.pencil),
				)}
				onClick={onToggle}
			>
				{editable ? 'Done' : 'Edit access'}
			</button>
		}
	</div>
)

const UserTeamsInner = (props: Props) => {
	const { data, error, loading, refetch } = useTeamsToAssignQuery(
		{ variables: {userId: props.user.id} }
	)
	const [editable, updateEditable] = useState(false)
	const globalContext = useGlobalContext()

	if (loading) {
		return (
			<>
				<Header />
				<div className={style.list}>
					<Placeholder style={{height: 96, width: '100%'}} />
					<Placeholder style={{height: 96, width: '100%'}} />
				</div>
			</>
		)
	}
	if (error || !data || !data.team_List || !data.userModule_List) {
		return (
			<>
				<Header />
				<div className={c(style.empty, style.error)}>Couldn't load access. Try refreshing the page.</div>
			</>
		)
	}

	// Module managers can only give access to the modules they manage, so the
	// rest are hidden while editing
	const manageableModuleIds = globalContext.user.organisation.suite.moduleSetItems
		.filter(msi => canManageTeams(globalContext.user, msi.module))
		.map(msi => msi.module.id)
	const manageableTeams = data.team_List.objects.filter(t => manageableModuleIds.includes(t.module.id))
	const teams = editable ? manageableTeams : data.team_List.objects
	const userModules = data.userModule_List.objects
	const hasAccess = teams.some(t => t.memberId) || userModules.length > 0

	return (
		<>
			<Header
				editable={editable}
				onToggle={manageableTeams.length > 0 ? () => updateEditable(!editable) : undefined}
			/>
			{editable &&
				<div className={style.hint}>
					Select what {props.user.firstName || 'this user'} can access.
				</div>
			}
			{teams.length === 0
				? <div className={style.empty}>There isn't anything to give access to yet.</div>
				: !editable && !hasAccess
					? (
						<div className={style.empty}>
							{props.user.firstName || 'This user'} doesn't have access to anything yet.
						</div>
					)
					: (
						<div className={style.list}>
							{groupByModule(teams).map(group => (
								<ModuleItem
									key={group.module.id}
									module={group.module}
									teams={group.teams}
									userModule={userModules.find(um => um.module.id === group.module.id) || null}
									user={props.user}
									editable={editable}
									refetch={refetch}
								/>
							))}
						</div>
					)
			}
		</>
	)
}

export default (props: Props) => {
	return (
		<div className={style.container}>
			{props.user.topLevelManager
				? (
					<>
						<Header />
						<div className={style.empty}>This user is an admin and has access to everything.</div>
					</>
				)
				: <UserTeamsInner {...props} />
			}
		</div>
	)
}
